import { useCallback, useEffect } from 'react';

import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { PrivacyScreen } from '@capacitor/privacy-screen';
import { Biometrics } from '@capawesome-team/capacitor-biometrics';

import { isNative } from '../helpers/utils.helper';
import { setCanUseBiometrics, setLockType } from '../pages/Login/loginSlice';
import { getPersistedLockType } from '../pages/Login/vault';
import { checkLocationPermission } from '../services/location.service';
import { checkNotificationPermission, upsertDevice } from '../services/notification.service';
import { useAppDispatch, useAppSelector } from '../store';

export const useVaultDevice = () => {
  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.login.authResult.token);
  const hasToken = Boolean(token);

  const updateDeviceInfo = useCallback(
    async (biometrics_enabled: boolean) => {
      if (hasToken) {
        const { isAvailable: biometrics_supported } = await Biometrics.isAvailable();
        const location_enabled = await checkLocationPermission();
        const notification_enabled = await checkNotificationPermission();
        await upsertDevice({ biometrics_supported, biometrics_enabled, location_enabled, notification_enabled });
      }
    },
    [hasToken],
  );

  const updateCanUseBiometrics = useCallback(async () => {
    const { isEnrolled: isBiometricsEnabled } = await Biometrics.isEnrolled();
    updateDeviceInfo(isBiometricsEnabled);
    dispatch(setCanUseBiometrics(isBiometricsEnabled));
  }, [dispatch, updateDeviceInfo]);

  useEffect(() => {
    if (isNative()) {
      updateCanUseBiometrics();
    }
  }, [updateCanUseBiometrics, hasToken]); // to trigger updates after logout/login

  useEffect(() => {
    let appListener: PluginListenerHandle;

    if (isNative()) {
      (async () => {
        if (Capacitor.isPluginAvailable('App')) {
          const { App } = await import('@capacitor/app');
          appListener = await App.addListener('appStateChange', async ({ isActive }) => {
            if (isActive) {
              await updateCanUseBiometrics();
            }
          });
        }
      })();
    }

    return () => {
      appListener?.remove();
    };
  }, [updateCanUseBiometrics]);

  useEffect(() => {
    if (isNative()) {
      PrivacyScreen.enable();
    }
  }, []);

  useEffect(() => {
    if (isNative()) {
      // sets the lock type after app restart
      (async () => {
        dispatch(setLockType(await getPersistedLockType()));
      })();
    }
  }, [dispatch, hasToken]); // to trigger updates after logout/login
};
